import { getTradeOfferLink } from '@/helper/steam.js';
import { OfferStatusEnum, OfferStatusGroupEnum } from '@/hook/market/trade.js';
import { websocketListen } from '@/hook/system/websocket.js';
import { Toast } from '@/hook/toast.js';
import { translate } from '@/main.js';
import { TRADE_TYPE } from '@common/enum/market.js';
import { WEBSOCKET_CHANNEL } from '@common/schema/ws.js';
import { getEnumName } from 'bambam-helper/enum.js';
import { pickFields } from 'bambam-util/cherrypick.js';
import { useMap } from 'bambam-util/map.js';
import { BamApiError } from 'bambam-vue-error';
import { computed, reactive } from 'vue';

export const WithdrawProgressEnum = {
  none: 0,
  selected: 1,
  waiting: 2,
  pending: 3,
  in_queue: 4,
  trade_link: 5,
  done: 6,
  failed: 7,
  error: 8,
};

const withdraw_map = useMap({
  createStore: () => reactive(new Map()),
  parseKey: (key) => String(key),
});

// @TODO: remove debug
window.withdraw_map = withdraw_map;

export const setWithdrawItemTrade = (id, tradeofferid) => {
  withdraw_map.mergeItem(id, {
    progress: WithdrawProgressEnum.trade_link,
    tradeofferid,
  });
};
export const setWithdrawItemError = (id, error) => {
  withdraw_map.mergeItem(id, {
    progress: WithdrawProgressEnum.error,
    error: new BamApiError(error),
  });
};
export const setWithdrawItemProgress = (id, progress, item) => {
  const data = {
    ...pickFields(item, ['app_id', 'asset_id']),
    progress,
  };
  withdraw_map.mergeItem(id, data);
};
export const clearWithdrawNot = (status) => {
  const ids = [];
  withdraw_map.getEntries().forEach(([key, item]) => {
    if (item.progress !== status) {
      ids.push(key);
      withdraw_map.deleteItem(key);
    }
  });
  return ids;
};
export const deleteWithdrawItemProgress = (id) => withdraw_map.deleteItem(id);
export const getWithdrawItemProgress = (id) => withdraw_map.getItem(id)?.progress || WithdrawProgressEnum.none;
export const getWithdrawItemProgressClass = (id) => `withdraw-progress-${getEnumName(WithdrawProgressEnum, getWithdrawItemProgress(id))}`;
export const getWithdrawItemProgressText = (id) => `market.withdraw_progress.${getEnumName(WithdrawProgressEnum, getWithdrawItemProgress(id))}`;
export const getWithdrawItemTrade = (id) => {
  const item = withdraw_map.getItem(id);
  if (item?.progress === WithdrawProgressEnum.trade_link) {
    return item.tradeofferid;
  }
  return null;
};
export const getWithdrawItemError = (id) => {
  const item = withdraw_map.getItem(id);
  if (item?.progress === WithdrawProgressEnum.error) {
    return item.error;
  }
  return null;
};
export const clearWithdrawItemProgress = () => withdraw_map.clearAll();
export const setWithdrawItemProgressAll = (progress) => {
  withdraw_map.mergeAll({ progress });
};
export const isWithdrawNeedClear = computed(() => withdraw_map.getEntries().some(([, data]) => data.progress !== WithdrawProgressEnum.selected && data.progress !== WithdrawProgressEnum.error));
const getActive = ({ assetid, appid }) => withdraw_map.getEntries().find(([, data]) => String(data.asset_id) === String(assetid) && String(appid) === String(data.app_id))?.[0];

const getTradeMessage = (trade_offer_state) => {
  if (trade_offer_state === 0 || trade_offer_state === OfferStatusEnum.CreatedNeedsConfirmation) {
    return translate('market.message.offer_preparing');
  }
  if (trade_offer_state === OfferStatusEnum.Active) {
    return translate('market.message.offer_active');
  }
  return translate('market.message.offer_sent');
};

const handleTrade = async (data) => {
  const state = getEnumName(OfferStatusEnum, data.trade_offer_state);

  data?.items_to_give?.forEach((item) => {
    const active = getActive(item);
    if (OfferStatusGroupEnum.failed.includes(data.trade_offer_state)) {
      setWithdrawItemProgress(active, WithdrawProgressEnum.failed);
    } else if (OfferStatusGroupEnum.completed.includes(data.trade_offer_state)) {
      setWithdrawItemProgress(active, WithdrawProgressEnum.done);
    } else {
      setWithdrawItemTrade(active, data.tradeofferid);
    }
  });

  if (data.trade_offer_state === -1) {
    Toast.toast({
      type: 'error',
      title: translate('market.message.withdraw', { hash: data.hash }),
      text: translate('market.message.failed_to_create_trade_offer', { state }),
      hideType: true,
      timeout: 15e3,
    });
    return;
  }

  if (OfferStatusGroupEnum.failed.includes(data.trade_offer_state)) {
    Toast.toast({
      type: 'error',
      title: translate('market.message.withdraw', { hash: data.hash }),
      text: translate('market.message.offer_failed', { state }),
      hideType: true,
      timeout: 15e3,
    });
    return;
  }

  if (OfferStatusGroupEnum.completed.includes(data.trade_offer_state)) {
    Toast.toast({
      type: 'success',
      title: translate('market.message.withdraw', { hash: data.hash }),
      text: translate('market.message.withdraw_confirmed'),
      hideType: true,
      timeout: 15e3,
    });
    return;
  }

  Toast.toast({
    type: 'info',
    title: translate('market.message.withdraw', { hash: data.hash }),
    text: getTradeMessage(data.trade_offer_state),
    hideType: true,
    timeout: 15e3,
    buttons: data.tradeofferid && data.trade_offer_state === OfferStatusEnum.Active ? [
      {
        text: translate('market.message.open_trade'),
        onClick: () => {
          window.open(getTradeOfferLink(data.tradeofferid));
        },
      },
      {
        text: translate('common.close'),
      },
    ] : [],
  });
};

websocketListen(WEBSOCKET_CHANNEL.steam.trade, (payload) => {
  if (payload?.trade_type !== TRADE_TYPE.withdraw) {
    return;
  }
  // if (payload.items_to_receive?.length) {
  //   return;
  // }
  // if (!payload.trade_offer_state) {
  //   return;
  // }

  setTimeout(() => {
    handleTrade(payload);
  }, 1e3);
});
